import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { AlertCircle, Clock, User, FileText, CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

// Complaints management component with resolution functionality

interface Complaint {
  id: string;
  guard_id: string;
  complaint_title: string;
  complaint_description: string;
  severity: string;
  status: string;
  complaint_date: string;
  created_at: string;
  resolution_notes?: string;
  resolved_at?: string;
  resolved_by?: string;
  guard_satisfied_with_resolution?: boolean;
  resolution_timestamp?: string;
  guard_name?: string;
}

interface ComplaintsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profileIds: string[];
}

export function ComplaintsDialog({ open, onOpenChange, profileIds }: ComplaintsDialogProps) {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [guardSatisfaction, setGuardSatisfaction] = useState<string>('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (open && profileIds.length > 0) {
      fetchComplaints();
    }
  }, [open, profileIds]);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      
      // First get complaints
      const { data: complaintsData, error: complaintsError } = await supabase
        .from('guard_complaints')
        .select(`
          id,
          guard_id,
          complaint_title,
          complaint_description,
          severity,
          status,
          complaint_date,
          created_at,
          resolution_notes,
          resolved_at,
          resolved_by,
          guard_satisfied_with_resolution,
          resolution_timestamp
        `)
        .in('guard_id', profileIds)
        .gte('complaint_date', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
        .order('created_at', { ascending: false });

      if (complaintsError) throw complaintsError;

      // Get guard names
      const guardIds = [...new Set(complaintsData?.map(c => c.guard_id) || [])];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name')
        .in('id', guardIds);

      // Combine data
      const complaintsWithNames = complaintsData?.map(complaint => ({
        ...complaint,
        guard_name: profilesData?.find(p => p.id === complaint.guard_id)?.full_name || 
                   `${profilesData?.find(p => p.id === complaint.guard_id)?.first_name} ${profilesData?.find(p => p.id === complaint.guard_id)?.last_name}` ||
                   'Άγνωστος'
      })) || [];

      setComplaints(complaintsWithNames);
    } catch (error) {
      console.error('Error fetching complaints:', error);
      toast.error('Σφάλμα κατά τη φόρτωση των παραπόνων');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveComplaint = async () => {
    if (!selectedComplaint || !resolutionNotes.trim()) {
      toast.error('Παρακαλώ εισάγετε σημειώσεις επίλυσης');
      return;
    }

    if (!guardSatisfaction) {
      toast.error('Παρακαλώ επιλέξτε αν ο φύλακας ικανοποιήθηκε');
      return;
    }

    setResolving(true);
    try {
      const { error } = await supabase
        .from('guard_complaints')
        .update({
          status: 'resolved',
          resolution_notes: resolutionNotes,
          resolved_at: new Date().toISOString(),
          resolved_by: (await supabase.auth.getUser()).data.user?.id,
          guard_satisfied_with_resolution: guardSatisfaction === 'satisfied',
          resolution_timestamp: new Date().toISOString()
        })
        .eq('id', selectedComplaint.id);

      if (error) throw error;

      toast.success('Το παράπονο επιλύθηκε επιτυχώς');
      setSelectedComplaint(null);
      setResolutionNotes('');
      setGuardSatisfaction('');
      fetchComplaints();
    } catch (error) {
      console.error('Error resolving complaint:', error);
      toast.error('Σφάλμα κατά την επίλυση του παραπόνου');
    } finally {
      setResolving(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'destructive';
      case 'in_progress': return 'default';
      case 'resolved': return 'secondary';
      default: return 'outline';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'Κρίσιμο';
      case 'high': return 'Υψηλό';
      case 'medium': return 'Μέτριο';
      case 'low': return 'Χαμηλό';
      default: return severity;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'Εκκρεμές';
      case 'in_progress': return 'Σε εξέλιξη';
      case 'resolved': return 'Επιλύθηκε';
      default: return status;
    }
  };

  // Resolution Dialog
  if (selectedComplaint) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Επίλυση Παραπόνου
            </DialogTitle>
            <DialogDescription>
              Προσθέστε σημειώσεις επίλυσης και καταγράψτε την ικανοποίηση του φύλακα
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">{selectedComplaint.complaint_title}</h4>
                    <div className="flex items-center gap-2">
                      <Badge variant={getSeverityColor(selectedComplaint.severity) as any}>
                        {getSeverityLabel(selectedComplaint.severity)}
                      </Badge>
                      <Badge variant={getStatusColor(selectedComplaint.status) as any}>
                        {getStatusLabel(selectedComplaint.status)}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      {selectedComplaint.guard_name}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {format(new Date(selectedComplaint.complaint_date), 'dd/MM/yyyy', { locale: el })}
                    </div>
                  </div>
                  
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-sm leading-relaxed">{selectedComplaint.complaint_description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Separator />

            <div className="space-y-4">
              <div>
                <Label htmlFor="resolution-notes" className="text-sm font-medium">
                  Σημειώσεις Επίλυσης *
                </Label>
                <Textarea
                  id="resolution-notes"
                  placeholder="Περιγράψτε τα μέτρα που ελήφθησαν για την επίλυση του παραπόνου..."
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  className="mt-1"
                  rows={4}
                />
              </div>

              <div>
                <Label className="text-sm font-medium">
                  Ικανοποίηση Φύλακα από την Επίλυση *
                </Label>
                <RadioGroup value={guardSatisfaction} onValueChange={setGuardSatisfaction} className="mt-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="satisfied" id="satisfied" />
                    <Label htmlFor="satisfied" className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Ικανοποιήθηκε
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="unsatisfied" id="unsatisfied" />
                    <Label htmlFor="unsatisfied" className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-600" />
                      Δεν ικανοποιήθηκε
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setSelectedComplaint(null)}
                disabled={resolving}
              >
                Ακύρωση
              </Button>
              <Button
                onClick={handleResolveComplaint}
                disabled={resolving || !resolutionNotes.trim() || !guardSatisfaction}
              >
                {resolving ? 'Επίλυση...' : 'Επίλυση Παραπόνου'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Main Complaints List Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Παράπονα Φυλάκων (Τελευταίες 90 ημέρες)
          </DialogTitle>
          <DialogDescription>
            Διαχείριση και επίλυση παραπόνων φυλάκων
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-20 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : complaints.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Δεν υπάρχουν παράπονα τις τελευταίες 90 ημέρες</p>
          </div>
        ) : (
          <div className="space-y-4">
            {complaints.map((complaint) => (
              <Card key={complaint.id} className="border-l-4 border-l-primary">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {complaint.complaint_title}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Badge variant={getSeverityColor(complaint.severity) as any}>
                        {getSeverityLabel(complaint.severity)}
                      </Badge>
                      <Badge variant={getStatusColor(complaint.status) as any}>
                        {getStatusLabel(complaint.status)}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <User className="h-4 w-4" />
                      <span>Φύλακας: {complaint.guard_name}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>
                        Ημερομηνία: {format(new Date(complaint.complaint_date), 'dd/MM/yyyy', { locale: el })}
                      </span>
                    </div>
                    
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <p className="text-sm leading-relaxed">{complaint.complaint_description}</p>
                    </div>

                    {complaint.status === 'resolved' && complaint.resolution_notes && (
                      <div className="mt-3 p-3 bg-green-50 rounded-md border-l-4 border-green-500">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-green-800">Επίλυση</p>
                          {complaint.guard_satisfied_with_resolution !== null && (
                            <div className="flex items-center gap-1 text-sm">
                              {complaint.guard_satisfied_with_resolution ? (
                                <>
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                  <span className="text-green-600">Ικανοποιημένος</span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 text-red-600" />
                                  <span className="text-red-600">Μη ικανοποιημένος</span>  
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-green-700">
                          {complaint.resolution_notes}
                        </p>
                        {complaint.resolved_at && (
                          <p className="text-xs text-green-600 mt-1">
                            Επιλύθηκε στις: {format(new Date(complaint.resolved_at), 'dd/MM/yyyy HH:mm', { locale: el })}
                          </p>
                        )}
                      </div>
                    )}

                    {complaint.status !== 'resolved' && (
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          onClick={() => setSelectedComplaint(complaint)}
                        >
                          Επίλυση Παραπόνου
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}